import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { VenueData, VenueTagData } from '../../../shared/dto/EntityRepository';
import {
  AlertPosition,
  AlertType,
  CountryType,
  SelectType,
  TextAlignment,
  VenueAdmissionType,
  VenueLifecycleStatus,
  VenueType,
} from '../../../shared/enum/EnumRepository';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtnHeartbeat, PaperBlurFitBtn } from '../../../shared/component/headless/button/BtnBody';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import HeadlessInput from '../../../shared/component/headless/input/HeadlessInput';
import { WorkViewerInfo, WorkViewerTitle } from '../../../shared/component/headless/input/InputBody';
import HeadlessTextArea from '../../../shared/component/headless/textarea/HeadlessTextArea';
import { WorkViewerInfoTextArea } from '../../../shared/component/headless/textarea/TextAreaBody';
import SelectBox from '../../../shared/component/SelectBox';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { VENUE_CODE_MAX_LENGTH, VENUE_CODE_PATTERN } from '../../../shared/constants/limits';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import editIcon from '../../../asset/icon/edit.png';
import seeIcon from '../../../asset/icon/see.png';
import deleteCloseIcon from '../../../asset/icon/delete_close.png';
import deleteOpenIcon from '../../../asset/icon/delete_open.png';
import { useAdminVenue } from '../../share/hook/useAdminVenue';
import { useAdminVenueTag } from '../../share/hook/useAdminVenueTag';
import AdminMemberInfo from '../members/AdminMemberInfo';
import { toInstagramHandle } from './instagramHandle';
import { useAdminVenueStore } from '../../share/store/adminVenueStore';
import { runAfterUnsavedVenueCheck } from './runAfterUnsavedVenueCheck';
import { isSameVenueDraft, isVenueDetail, normalizeVenueDetail, toUpdateVenueReq } from './toUpdateVenueReq';
import VenueTagInViewer from './VenueTagInViewer';

const isMissingRequired = (detail: VenueData, requireCode: boolean) =>
  (requireCode && !detail.venueCode.trim())
  || !detail.name.trim()
  || !detail.type
  || !detail.countryCode.trim()
  || !detail.city.trim()
  || !detail.roadAddress.trim()
  || !detail.timeZone.trim();

const VenueViewer: React.FC = () => {
  const { activeVenue, setActiveVenue, create, update, remove, bulkResult } = useAdminVenue();
  const setHasChanged = useAdminVenueStore((state) => state.setHasChanged);
  const { tags: venueTags, status: venueTagStatus } = useAdminVenueTag();
  const enqueueAlert = useStandardAlertStore((state) => state.enqueueAlert);
  const [isEditing, setIsEditing] = useState(false);
  const originalVenueRef = useRef<VenueData | null>(null);
  const createBaselineRef = useRef<VenueData | null>(null);

  const isCreating = !!activeVenue && !activeVenue.id;
  const detail = activeVenue && isVenueDetail(activeVenue) ? activeVenue : null;
  const hasChanged = (() => {
    if (!detail) return false;
    if (isCreating && createBaselineRef.current) {
      return !isSameVenueDraft(createBaselineRef.current, detail);
    }
    if (isEditing && originalVenueRef.current) {
      return !isSameVenueDraft(originalVenueRef.current, detail);
    }
    return false;
  })();

  useEffect(() => {
    setIsEditing(false);
    originalVenueRef.current = null;
    createBaselineRef.current = isCreating && detail
      ? normalizeVenueDetail(detail)
      : null;
    // id가 바뀔 때만 생성 초안 기준점을 잡는다. detail을 deps에 넣으면 입력마다 기준점이 덮인다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVenue?.id]);

  useEffect(() => {
    setHasChanged(hasChanged);
  }, [hasChanged, setHasChanged]);

  useEffect(() => () => {
    useAdminVenueStore.getState().setHasChanged(false);
  }, []);

  if (!activeVenue) return null;

  const updateDraft = (updates: Partial<VenueData>) => {
    if (!detail) return;
    setActiveVenue({ ...detail, ...updates });
  };

  const enterEdit = () => {
    if (!detail) return;
    const normalized = normalizeVenueDetail(detail);
    originalVenueRef.current = normalized;
    setActiveVenue(normalized);
    setIsEditing(true);
  };

  const exitEdit = () => {
    if (originalVenueRef.current) {
      const originalVenue = originalVenueRef.current;
      const currentTagById = new Map(venueTags.map((tag) => [tag.id, tag]));
      setActiveVenue({
        ...originalVenue,
        tags: venueTagStatus === 'success'
          ? originalVenue.tags.flatMap((tag) => {
              const currentTag = currentTagById.get(tag.id);
              return currentTag ? [currentTag] : [];
            })
          : originalVenue.tags,
      });
    }
    originalVenueRef.current = null;
    setIsEditing(false);
  };

  const handleCreate = () => {
    if (!detail) return;
    const venueCode = detail.venueCode.trim();
    if (isMissingRequired(detail, true)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('MISSING_REQUIRED_FIELD'),
      });
      return;
    }
    if (
      venueCode.length > VENUE_CODE_MAX_LENGTH
      || !VENUE_CODE_PATTERN.test(venueCode)
    ) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_VALUE_REQUESTED'),
      });
      return;
    }

    create({
      venueCode,
      name: detail.name.trim(),
      type: detail.type,
      countryCode: detail.countryCode.trim(),
      city: detail.city.trim(),
      roadAddress: detail.roadAddress.trim(),
      timeZone: detail.timeZone.trim(),
      nameEn: detail.nameEn.trim() || undefined,
      district: detail.district.trim() || undefined,
      roadAddressEn: detail.roadAddressEn.trim() || undefined,
      admissionType: detail.admissionType,
      instagramUrl: detail.instagramUrl.trim() || undefined,
      websiteUrl: detail.websiteUrl.trim() || undefined,
      phoneNumber: detail.phoneNumber.trim() || undefined,
      description: detail.description.trim() || undefined,
      descriptionEn: detail.descriptionEn.trim() || undefined,
    });
  };

  const handleUpdate = async () => {
    if (!detail) return;
    if (isMissingRequired(detail, false)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('MISSING_REQUIRED_FIELD'),
      });
      return;
    }
    if ((detail.latitude == null) !== (detail.longitude == null)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_VALUE_REQUESTED'),
      });
      return;
    }

    const saved = await update(detail.id, toUpdateVenueReq(detail));
    if (!saved) return;
    originalVenueRef.current = null;
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (!detail) return;
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_DELETE_VENUE', { name: detail.name }),
      callBack: () => { void remove(detail.id); },
    });
  };

  const parseCoord = (raw: string): number | null | undefined => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const handleTagToggle = (tag: VenueTagData) => {
    if (!detail) return;
    const currentTags = detail.tags ?? [];
    updateDraft({
      tags: currentTags.some((item) => item.id === tag.id)
        ? currentTags.filter((item) => item.id !== tag.id)
        : [...currentTags, tag],
    });
  };

  if ((isCreating || isEditing) && detail) {
    return (
      <ViewerContainer data-has-changed={hasChanged ? 'true' : 'false'}>
        <ScrollBody>
          <FormContainer>
          <HeadlessInput
            value={detail.name}
            handleChange={(e) => updateDraft({ name: e.target.value })}
            placeholder="Venue name"
            StyledInput={TitleInput}
          />
          <HeadlessInput
            value={detail.nameEn}
            handleChange={(e) => updateDraft({ nameEn: e.target.value })}
            placeholder="Venue name (EN)"
            StyledInput={SubtitleInput}
          />
          <InfoForm>
          <FieldRow>
            <FieldLabel>Code</FieldLabel>
            {isCreating ? (
              <FieldValue>
                <HeadlessInput
                  value={detail.venueCode}
                  handleChange={(e) => updateDraft({
                    venueCode: e.target.value.slice(0, VENUE_CODE_MAX_LENGTH),
                  })}
                  placeholder="MMCA-SEOUL"
                  StyledInput={VenueFormInput}
                />
              </FieldValue>
            ) : (
              <ReadOnlyValue>{detail.venueCode}</ReadOnlyValue>
            )}
          </FieldRow>
          <FieldRow>
            <FieldLabel>Type</FieldLabel>
            <SelectBoxWrapper>
              <SelectBox
                value={detail.type}
                options={Object.values(VenueType)}
                handleChange={(value: VenueType) => updateDraft({ type: value })}
                direction={false}
              />
            </SelectBoxWrapper>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Admission</FieldLabel>
            <SelectBoxWrapper>
              <SelectBox
                value={detail.admissionType}
                options={Object.values(VenueAdmissionType)}
                handleChange={(value: VenueAdmissionType) =>
                  updateDraft({ admissionType: value })
                }
                direction={false}
              />
            </SelectBoxWrapper>
          </FieldRow>
          {isEditing && (
            <>
              <FieldRow>
                <FieldLabel>Status</FieldLabel>
                <SelectBoxWrapper>
                  <SelectBox
                    value={detail.lifecycleStatus}
                    options={Object.values(VenueLifecycleStatus)}
                    handleChange={(value: VenueLifecycleStatus) =>
                      updateDraft({ lifecycleStatus: value })
                    }
                    direction={false}
                  />
                </SelectBoxWrapper>
              </FieldRow>
              <FieldBlock>
                <FieldLabel>Label</FieldLabel>
                <TagContainer>
                  {venueTags.map((tag) => (
                    <VenueTagInViewer
                      key={tag.id}
                      tag={tag}
                      selectedTags={detail.tags}
                      isEditMode={true}
                      onToggle={handleTagToggle}
                    />
                  ))}
                </TagContainer>
              </FieldBlock>
            </>
          )}
          <FieldRow>
            <FieldLabel>Country</FieldLabel>
            <CountrySelectBoxWrapper>
              <SelectBox
                value={detail.countryCode as CountryType}
                selectType={SelectType.COUNTRY}
                handleChange={(value: CountryType) =>
                  updateDraft({ countryCode: value })
                }
                direction={false}
                isOptionDisabled={(value) => value === CountryType.NONE}
              />
            </CountrySelectBoxWrapper>
          </FieldRow>
          <FieldRow>
            <FieldLabel>City</FieldLabel>
            <FieldValue>
              <HeadlessInput
                value={detail.city}
                handleChange={(e) => updateDraft({ city: e.target.value })}
                placeholder="Seoul"
                StyledInput={VenueFormInput}
              />
            </FieldValue>
          </FieldRow>
          <FieldRow>
            <FieldLabel>District</FieldLabel>
            <FieldValue>
              <HeadlessInput
                value={detail.district}
                handleChange={(e) => updateDraft({ district: e.target.value })}
                placeholder="District"
                StyledInput={VenueFormInput}
              />
            </FieldValue>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Address</FieldLabel>
            <FieldValue>
              <HeadlessInput
                value={detail.roadAddress}
                handleChange={(e) => updateDraft({ roadAddress: e.target.value })}
                placeholder="Road address"
                StyledInput={VenueFormInput}
              />
            </FieldValue>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Address EN</FieldLabel>
            <FieldValue>
              <HeadlessInput
                value={detail.roadAddressEn}
                handleChange={(e) => updateDraft({ roadAddressEn: e.target.value })}
                placeholder="Road address (EN)"
                StyledInput={VenueFormInput}
              />
            </FieldValue>
          </FieldRow>
          {isEditing && (
            <>
              <FieldRow>
                <FieldLabel>Latitude</FieldLabel>
                <FieldValue>
                  <HeadlessInput
                    value={detail.latitude ?? ''}
                    handleChange={(e) => {
                      const latitude = parseCoord(e.target.value);
                      if (latitude !== undefined) updateDraft({ latitude });
                    }}
                    placeholder="37.5665"
                    StyledInput={VenueFormInput}
                  />
                </FieldValue>
              </FieldRow>
              <FieldRow>
                <FieldLabel>Longitude</FieldLabel>
                <FieldValue>
                  <HeadlessInput
                    value={detail.longitude ?? ''}
                    handleChange={(e) => {
                      const longitude = parseCoord(e.target.value);
                      if (longitude !== undefined) updateDraft({ longitude });
                    }}
                    placeholder="126.9780"
                    StyledInput={VenueFormInput}
                  />
                </FieldValue>
              </FieldRow>
            </>
          )}
          <FieldRow>
            <FieldLabel>Time zone</FieldLabel>
            <FieldValue>
              <HeadlessInput
                value={detail.timeZone}
                handleChange={(e) => updateDraft({ timeZone: e.target.value })}
                placeholder="Asia/Seoul"
                StyledInput={VenueFormInput}
              />
            </FieldValue>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Phone</FieldLabel>
            <FieldValue>
              <HeadlessInput
                value={detail.phoneNumber}
                handleChange={(e) => updateDraft({ phoneNumber: e.target.value })}
                placeholder="Phone number"
                StyledInput={VenueFormInput}
              />
            </FieldValue>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Instagram</FieldLabel>
            <FieldValue>
              <HeadlessInput
                value={detail.instagramUrl}
                handleChange={(e) => updateDraft({ instagramUrl: e.target.value })}
                placeholder="Instagram URL"
                StyledInput={VenueFormInput}
              />
            </FieldValue>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Website</FieldLabel>
            <FieldValue>
              <HeadlessInput
                value={detail.websiteUrl}
                handleChange={(e) => updateDraft({ websiteUrl: e.target.value })}
                placeholder="Website URL"
                StyledInput={VenueFormInput}
              />
            </FieldValue>
          </FieldRow>
          {isEditing && (
            <>
              <FieldRow>
                <FieldLabel>Naver map</FieldLabel>
                <FieldValue>
                  <HeadlessInput
                    value={detail.naverMapUrl}
                    handleChange={(e) => updateDraft({ naverMapUrl: e.target.value })}
                    placeholder="Naver map URL"
                    StyledInput={VenueFormInput}
                  />
                </FieldValue>
              </FieldRow>
              <FieldRow>
                <FieldLabel>Google map</FieldLabel>
                <FieldValue>
                  <HeadlessInput
                    value={detail.googleMapUrl}
                    handleChange={(e) => updateDraft({ googleMapUrl: e.target.value })}
                    placeholder="Google map URL"
                    StyledInput={VenueFormInput}
                  />
                </FieldValue>
              </FieldRow>
              <FieldBlock>
                <FieldLabel>Admission note</FieldLabel>
                <FieldValue>
                  <HeadlessTextArea
                    alignment={TextAlignment.LEFT}
                    content={detail.admissionNote}
                    placeholder="Admission note"
                    handleChange={(e) => updateDraft({ admissionNote: e.target.value })}
                    StyledTextArea={VenueFormTextArea}
                    $minRows={1}
                    $maxRows={6}
                  />
                </FieldValue>
              </FieldBlock>
              <FieldBlock>
                <FieldLabel>Hours note</FieldLabel>
                <FieldValue>
                  <HeadlessTextArea
                    alignment={TextAlignment.LEFT}
                    content={detail.openingHoursNote}
                    placeholder="Hours note"
                    handleChange={(e) => updateDraft({ openingHoursNote: e.target.value })}
                    StyledTextArea={VenueFormTextArea}
                    $minRows={1}
                    $maxRows={6}
                  />
                </FieldValue>
              </FieldBlock>
            </>
          )}
          </InfoForm>
          <FieldBlock>
            <FieldLabel>Description</FieldLabel>
            <FieldValue>
              <HeadlessTextArea
                alignment={TextAlignment.LEFT}
                content={detail.description}
                placeholder="Description"
                handleChange={(e) => updateDraft({ description: e.target.value })}
                StyledTextArea={VenueFormTextArea}
                $minRows={1}
                $maxRows={6}
              />
            </FieldValue>
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>Description EN</FieldLabel>
            <FieldValue>
              <HeadlessTextArea
                alignment={TextAlignment.LEFT}
                content={detail.descriptionEn}
                placeholder="Description (EN)"
                handleChange={(e) => updateDraft({ descriptionEn: e.target.value })}
                StyledTextArea={VenueFormTextArea}
                $minRows={1}
                $maxRows={6}
              />
            </FieldValue>
          </FieldBlock>
          {isCreating && (
            <BtnContainer>
              <HeadlessBtn
                value="Create"
                handleClick={handleCreate}
                StyledBtn={BlackFitBtnHeartbeat}
              />
              <HeadlessBtn
                value="Cancel"
                handleClick={() => runAfterUnsavedVenueCheck(() => setActiveVenue(null))}
                StyledBtn={PaperBlurFitBtn}
              />
            </BtnContainer>
          )}
        </FormContainer>
        </ScrollBody>
        {isEditing && (
          <ActionBar>
            {hasChanged && (
              <HeadlessBtn
                value="Update"
                handleClick={() => { void handleUpdate(); }}
                StyledBtn={BlackFitBtnHeartbeat}
              />
            )}
            <UniversalIconBtn
              size="fit"
              border={true}
              backgroundImg={seeIcon}
              onClick={exitEdit}
            />
          </ActionBar>
        )}
      </ViewerContainer>
    );
  }

  return (
    <ViewerContainer>
      <ScrollBody>
      {bulkResult && (
        <HeadlessBtn
          value="Results"
          handleClick={() => setActiveVenue(null)}
          StyledBtn={BackBtn}
        />
      )}
      <Title>{activeVenue.name}</Title>
      {activeVenue.nameEn && <Meta>{activeVenue.nameEn}</Meta>}
      <InfoContainer>
        <AdminMemberInfo label="Code" value={activeVenue.venueCode || '-'} />
        <AdminMemberInfo label="Type" value={activeVenue.type} />
        <AdminMemberInfo label="Admission" value={activeVenue.admissionType} />
        {detail && (
          <>
            <AdminMemberInfo label="Status" value={detail.lifecycleStatus} />
            {detail.tags.length > 0 && (
              <FieldBlock>
                <FieldLabel>Label</FieldLabel>
                <TagContainer>
                  {detail.tags.map((tag) => (
                    <VenueTagInViewer
                      key={tag.id}
                      tag={tag}
                      selectedTags={detail.tags}
                      isEditMode={false}
                      onToggle={handleTagToggle}
                    />
                  ))}
                </TagContainer>
              </FieldBlock>
            )}
            <AdminMemberInfo label="Country" value={detail.countryCode} />
            <AdminMemberInfo label="City" value={detail.city} />
            <AdminMemberInfo label="District" value={detail.district || '-'} />
            <AdminMemberInfo label="Address" value={detail.roadAddress} />
            <AdminMemberInfo label="Address EN" value={detail.roadAddressEn || '-'} />
            <AdminMemberInfo
              label="Coordinates"
              value={
                detail.latitude != null && detail.longitude != null
                  ? `${detail.latitude}, ${detail.longitude}`
                  : '-'
              }
            />
            <AdminMemberInfo label="Time zone" value={detail.timeZone} />
            <AdminMemberInfo
              label="Instagram"
              value={toInstagramHandle(detail.instagramUrl)}
              href={
                toInstagramHandle(detail.instagramUrl) !== '-'
                  ? detail.instagramUrl
                  : undefined
              }
            />
            <AdminMemberInfo
              label="Website"
              value={detail.websiteUrl || '-'}
              href={detail.websiteUrl || undefined}
            />
            <AdminMemberInfo label="Phone" value={detail.phoneNumber || '-'} />
            <AdminMemberInfo
              label="Naver map"
              value={detail.naverMapUrl || '-'}
              href={detail.naverMapUrl || undefined}
            />
            <AdminMemberInfo
              label="Google map"
              value={detail.googleMapUrl || '-'}
              href={detail.googleMapUrl || undefined}
            />
            <AdminMemberInfo label="Admission note" value={detail.admissionNote || '-'} />
            <AdminMemberInfo label="Hours note" value={detail.openingHoursNote || '-'} />
            <HoursRow>
              <HoursLabel>Hours:</HoursLabel>
              <HoursValue>
                {detail.openingHours?.length
                  ? detail.openingHours.map((hour) => (
                    <HoursLine key={hour.dayOfWeek}>
                      <span>{hour.dayOfWeek}</span>
                      <span>
                        {hour.closed
                          ? 'closed'
                          : `${hour.opensAt}–${hour.closesAt}`}
                      </span>
                    </HoursLine>
                  ))
                  : '-'}
              </HoursValue>
            </HoursRow>
          </>
        )}
      </InfoContainer>
      {detail?.description && (
        <Description>{detail.description}</Description>
      )}
      {detail?.descriptionEn && (
        <Description>{detail.descriptionEn}</Description>
      )}
      </ScrollBody>
      {detail && (
        <ActionBar>
          <UniversalIconBtn
            size="fit"
            border={true}
            backgroundImg={editIcon}
            onClick={enterEdit}
          />
          <UniversalIconBtn
            size="fit"
            border={true}
            backgroundImg={deleteCloseIcon}
            backgroundImgHover={deleteOpenIcon}
            onClick={handleDelete}
          />
        </ActionBar>
      )}
    </ViewerContainer>
  );
};

const ViewerContainer = styled.section`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScrollBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  padding-bottom: 56px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 0 16px;
`;

const InfoForm = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 2px;
  gap: 6px;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const TitleInput = styled(WorkViewerTitle)`
  margin-bottom: 0;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const SubtitleInput = styled(WorkViewerInfo)`
  margin-bottom: 8px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  color: ${({ theme }) => theme.colors.color_Gray_05};
`;

const FieldRow = styled.div`
  min-height: 18px;
  margin-bottom: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const FieldBlock = styled.div`
  height: fit-content;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const FieldLabel = styled.span`
  flex-shrink: 0;
  min-width: 140px;
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const FieldValue = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const ReadOnlyValue = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_01};
`;

const VenueFormInput = styled(WorkViewerInfo)`
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const VenueFormTextArea = styled(WorkViewerInfoTextArea)`
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const SelectBoxWrapper = styled.article`
  flex: 1 1 auto;
  min-width: 10ch;
  max-width: 22ch;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const CountrySelectBoxWrapper = styled.article`
  flex: 0 1 8ch;
  min-width: 6ch;
  max-width: 8ch;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const TagContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BackBtn = styled.button`
  align-self: flex-start;
  padding: 0 0 8px;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};

  &:hover {
    color: ${({ theme }) => theme.colors.color_Gray_03};
  }
`;

const Title = styled.h2`
  width: 100%;
  min-height: 28px;
  margin: 0;
  display: flex;
  align-items: center;
  padding-bottom: 1px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const Meta = styled.div`
  margin-bottom: 14px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HoursRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding-right: 4px;
  margin-bottom: 1px;
  ${({ theme }) => theme.typography.Body_04};
`;

const HoursLabel = styled.span`
  min-width: 140px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const HoursValue = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.color_Gray_01};
`;

const HoursLine = styled.div`
  min-height: 18px;
  display: grid;
  grid-template-columns: 11ch 1fr;
  column-gap: 12px;
`;

const Description = styled.div`
  padding: 12px 0 9px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const BtnContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vh;
  padding-top: 8px;
`;

const ActionBar = styled.div`
  position: absolute;
  right: 0;
  bottom: 0.6vh;
  z-index: 1;
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vh;
`;

export default VenueViewer;
